import { AxiosError } from 'axios';
import { Brands } from './enums/brands.enum';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import { Location } from 'react-router-dom';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import { PERMISSIONS_DEALER, PERMISSIONS_NAME } from '../config';

const FIELD_USER = 'field';

export function getUrlParameter(
    location: Location,
    name: string,
): string | null {
    const params = new URLSearchParams(location.search);
    return params.get(name);
}

export function isBadRequestResponse(err: AxiosError) {
    return err.response && err.response.status == HttpStatusCode.BAD_REQUEST;
}

export function isSiteCanadian(): boolean {
    const domain = location.host.split('.');
    return domain.includes('vwfinance');
}

export function isValidDealerPortal(country: string): boolean {
    const host = window.location.origin;
    const siteIsCanadian = isSiteCanadian();
    const isLocalhost = host.includes('localhost');
    const isValid =
        isLocalhost ||
        country === 'NA' ||
        (siteIsCanadian ? country === 'CA' : country === 'US');

    return isValid;
}

export function isAllow(
    permissions: Array<string>,
    permissionsMap: Map<string, Array<string>>,
    dealer?: string,
): boolean {
    for (const permission of permissions) {
        const dealers = permissionsMap.get(permission);

        if (
            dealers?.includes(FIELD_USER) ||
            (dealer && dealers?.includes(dealer))
        ) {
            return true;
        }
    }

    return false;
}

export function getPermissionsMap(): Map<string, Array<string>> {
    const map = new Map<string, Array<string>>();

    const parseJSON = (key: string): any[] => {
        const item = sessionStorage.getItem(key);
        if (!item) return [];
        try {
            return JSON.parse(item);
        } catch {
            return [];
        }
    };

    const combinedPermissions = [
        ...parseJSON(PERMISSIONS_DEALER),
        ...parseJSON(PERMISSIONS_NAME),
    ];

    combinedPermissions.forEach((e: any) => {
        const dealerId =
            e.dealerId === null || e.dealerId === undefined
                ? FIELD_USER
                : e.dealerId;
        const dealers = map.get(e.permission);

        if (dealers) {
            dealers.push(dealerId);
        } else {
            map.set(e.permission, [dealerId]);
        }
    });

    return map;
}

export function formatDate(dateString: string) {
    /*eslint no-magic-numbers: ["error", { "ignore": [1] }]*/
    const date = dateString ? new Date(dateString) : new Date();
    const year = date.getFullYear();
    let month = (date.getMonth() + 1).toString();
    month = month.length > 1 ? month : '0' + month;
    let day = date.getDate().toString();
    day = day.length > 1 ? day : '0' + day;
    const isCanadian = isSiteCanadian();

    return isCanadian ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}

export function dialogModalHandler(
    event: any,
    dispatch: Function,
    dialogModal: any,
) {
    if (dialogModal.isModalActive) {
        event.preventDefault();
        const MILISECONDS_TO_WAIT = 50;
        const cancelHandler = () => {
            dispatch({
                type: MODAL_CONTROL_ACTIONS.SET_SHOW,
                payload: { shouldShow: false },
            });
        };
        const confirmHandler = () => {
            dispatch({
                type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
            });
            setTimeout(() => event.target.click(), MILISECONDS_TO_WAIT);
        };
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: {
                shouldShow: true,
                onCancelHandler: cancelHandler,
                onConfirmHandler: confirmHandler,
            },
        });
    }
}

export function maskDigits(input: string): string {
    return input ? input.replace(/(\d)(?=(\D*\d){4})/g, 'X') : '';
}

/**
 * Function to perform string sorting the array with the key value
 * @param items
 * @param key
 * @param sortOrder
 * @returns
 */
export const compareStrings = (
    items: any[],
    key: string,
    sortOrder: boolean,
) => {
    return items.sort((a, b) => {
        return sortOrder
            ? a[key].localeCompare(b[key])
            : b[key].localeCompare(a[key]);
    });
};

/**
 * Function to perform Float sorting the array with the key value
 * @param items
 * @param key
 * @param sortOrder
 * @returns
 */
export const compareFloat = (items: any[], key: string, sortOrder: boolean) => {
    return items.sort((a, b) => {
        return sortOrder
            ? parseFloat(a[key]) - parseFloat(b[key])
            : parseFloat(b[key]) - parseFloat(a[key]);
    });
};

/**
 * Function to perform number sorting the array with the key value
 * @param items
 * @param key
 * @param sortOrder
 * @returns
 */
export const compareNumbers = (
    items: any[],
    key: string,
    sortOrder: boolean,
) => {
    return items.sort((a, b) => {
        return sortOrder ? a[key] - b[key] : b[key] - a[key];
    });
};

/**
 * Function to perform date sorting the array with the key value
 * @param items
 * @param key
 * @param sortOrder
 * @returns
 */
export const compareDates = (items: any[], key: string, sortOrder: boolean) => {
    return items.sort((a, b) => {
        return sortOrder
            ? new Date(b[key]).getTime() - new Date(a[key]).getTime()
            : new Date(a[key]).getTime() - new Date(b[key]).getTime();
    });
};

export function isBrandVW(brand: string): boolean {
    return (
        Brands.VW === brand.toUpperCase() ||
        Brands.Volkswagen.toUpperCase() === brand.toUpperCase()
    );
}

export function isBrandAudi(brand: string): boolean {
    return Brands.AUDI.toUpperCase() === brand.toUpperCase();
}

export function isBrandDucati(brand: string): boolean {
    return Brands.DUCATI.toUpperCase() === brand.toUpperCase();
}

/**
 * Returs brand name ducati if dealer number starts with 71
 * @param brand
 * @param dealerNumber
 * @returns
 */
export function getBrandByDealerNumber(
    brand: string,
    dealerNumber: string,
): string {
    const isDucatiBrandByDealerNumber =
        dealerNumber?.startsWith?.('71') ?? false;
    return isDucatiBrandByDealerNumber
        ? Brands.DUCATI.toLocaleLowerCase()
        : brand.toLocaleLowerCase();
}
export function removesNullOrUndefined(obj: any) {
    const cleaned = Object.fromEntries(
        Object.entries(obj).filter((entry) => {
            const value = entry[1];
            return value !== null && value !== undefined && value !== '';
        }),
    );

    return !isEmptyObject(cleaned) ? cleaned : undefined;
}

export function isEmptyObject(obj: any) {
    const ZERO = 0;
    return Object.keys(obj).length === ZERO;
}

/**
 * Returns footer link based on pagename and language
 * @param page
 * @param language
 * @returns
 */
export const getFooterLink = (
    page: 'privacy' | 'usage' | 'contact',
    language: 'en' | 'fr' = 'en',
    isBrandSpecific?: boolean,
    brand?: string,
) => {
    const env = process.env.REACT_APP_DEALER_PORTAL_ENV || 'dev';
    const lang = language === 'fr' ? 'frca' : 'enca';
    const hostURL = isSiteCanadian()
        ? 'dealerportal.vwfinance.ca'
        : 'dealerportal.vwcredit.com';
    const subdomain = env === 'prod' ? 'content' : `content.${env}`;
    const brandParam =
        isBrandSpecific && !isSiteCanadian() ? `?brand=${brand}&` : '?';
    const link = `https://${subdomain}.${hostURL}/${brandParam}lang=${lang}#${page}`;

    return link;
};

/**
 * Function to restrict special char in input type number
 * @param e
 */
export const restrictNonNumericInputOnKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
) => {
    if (['e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault();
    }
};

export const handleKeyDownYearMonth = (
    e: React.KeyboardEvent<HTMLInputElement>,
) => {
    if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault();
    }
};

export const safeString = (value: any): string => String(value ?? '');

/**
 * Function to check min password lenght
 * @param password
 * @returns
 */
export function isPasswordMinLength(password: string) {
    return password && /^.{7,}$/.test(password);
}

/**
 * Function to check if password has at least one letter, one number, and one special character
 * @param password
 * @returns
 */
export function hasPasswordRequiredChars(password: string) {
    return (
        password && /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(password)
    );
}

/**
 * Function to check if password does NOT have 3 repeated characters or repeated 3+ character sequences
 * @param password
 * @returns
 */
export function hasPasswordNoRepeats(password: string) {
    const tripleCharRepeat = /(.)\1{2}/;
    const repeatedSequence = /(.{3,})\1/;
    return (
        password &&
        !tripleCharRepeat.test(password) &&
        !repeatedSequence.test(password)
    );
}

/**
 * Return the password note description
 * @param password
 * @param accountRegLocalization
 * @returns
 */
export const getPasswordFieldDesc = (
    password: string,
    accountRegLocalization: any,
) => {
    const validations = [
        {
            isValid: isPasswordMinLength(password),
            message: accountRegLocalization.ACC_REG_LABEL_PASSWORD_NOTE1,
        },
        {
            isValid: hasPasswordRequiredChars(password),
            message: accountRegLocalization.ACC_REG_LABEL_PASSWORD_NOTE2,
        },
        {
            isValid: hasPasswordNoRepeats(password),
            message: accountRegLocalization.ACC_REG_LABEL_PASSWORD_NOTE3,
        },
    ];

    const validationMessages = validations
        .map(
            ({ isValid, message }) =>
                `<i class='c-icon xxsmall c-icon--[semantic-${
                    isValid ? 'checkmark' : 'close'
                }]' aria-hidden='true' role='img'></i>${message}`,
        )
        .join('<br>');

    return `${accountRegLocalization.ACC_REG_LABEL_PASSWORD_NOTE_HEADING}<br>${validationMessages}`;
};

/**
 *
 * @returns true if system admin feature is enabled in pipeline
 */
export const isSystemAdminUIEnabled = () =>
    process.env.REACT_APP_ENABLE_SYSTEM_ADMIN_FEATURE === 'true';

/**
 * Function to get the number value from the string
 * @param value
 * @returns
 */
export const numberOrDefault = (value: string | number) => {
    const num = Number(value);
    const ZERO = 0;
    return !isNaN(num) ? num : ZERO;
};

export const countryMappper = (country: string) => {
    if (!country) {
        return '';
    }

    const countryMap: Record<string, string> = {
        US: 'USA',
        CA: 'Canada',
        USA: 'USA',
        CANADA: 'Canada',
    };

    return countryMap[country.toUpperCase()];
};

/**
 * Tags that never require a closing tag. Used by cropHtml so they are not
 * pushed onto the open-tag stack and therefore never get closed on crop.
 */
const VOID_HTML_TAGS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'param',
    'source',
    'track',
    'wbr',
]);

const CROP_ELLIPSIS = '\u2026';
const CROP_ZERO = 0;
const CROP_ONE = 1;
const CROP_NOT_FOUND = -1;

/**
 * Computes the visible-text length of an HTML string, ignoring tag markup.
 * @param html
 * @returns number of visible characters
 */
const getVisibleTextLength = (html: string): number => {
    return html.replace(/<[^>]*>/g, '').length;
};

/**
 * Backs a kept text segment up to the last whole-word boundary and trims any
 * trailing whitespace. Falls back to a hard cut when a single word is longer
 * than the allowed length (i.e. no whitespace to back up to).
 * @param text the visible text that was kept (already limited to maxLength)
 * @returns the word-boundary-aware text without trailing whitespace
 */
const backUpToWordBoundary = (text: string): string => {
    const lastSpaceIndex = text.search(/\s\S*$/);
    if (lastSpaceIndex === CROP_NOT_FOUND) {
        // Single word longer than the limit: keep the hard cut.
        return text.replace(/\s+$/, '');
    }
    return text.slice(CROP_ZERO, lastSpaceIndex).replace(/\s+$/, '');
};

/**
 * Crops an HTML message to a maximum visible-text length without breaking
 * tags. Only visible characters (text between tags) count toward the limit.
 * When cropping occurs the text is backed up to the last whole word, an
 * ellipsis is appended, and any still-open tags are closed in reverse order.
 * Void/self-closing tags (br, img, hr, ...) are never closed. If the visible
 * text already fits within maxLength the original string is returned as-is.
 * @param html the (trusted) HTML string to crop
 * @param maxLength maximum number of visible characters to keep
 * @returns the cropped, tag-balanced HTML string
 */
export function cropHtml(html: string, maxLength: number): string {
    const value = safeString(html);
    const EMPTY = '';

    if (!value) {
        return EMPTY;
    }

    // Nothing to crop: visible text already fits.
    if (maxLength > CROP_ZERO && getVisibleTextLength(value) <= maxLength) {
        return value;
    }

    const openTags: string[] = [];
    const tagPattern = /<[^>]*>/g;
    let output = EMPTY;
    let visibleCount = CROP_ZERO;
    let cursor = CROP_ZERO;
    let cropped = false;
    let match: RegExpExecArray | null;

    const consumeText = (text: string): boolean => {
        const remaining = maxLength - visibleCount;
        if (text.length <= remaining) {
            output += text;
            visibleCount += text.length;
            return false;
        }
        // Reached the limit inside this text segment. Only back up to a word
        // boundary when the cut would split a word (i.e. the char right after
        // the cut is not whitespace); otherwise the cut is already clean.
        const slice = text.slice(CROP_ZERO, remaining);
        const nextChar = text.charAt(remaining);
        const cutsMidWord = nextChar !== '' && !/\s/.test(nextChar);
        const kept = cutsMidWord
            ? backUpToWordBoundary(slice)
            : slice.replace(/\s+$/, '');
        output += kept + CROP_ELLIPSIS;
        cropped = true;
        return true;
    };

    while ((match = tagPattern.exec(value)) !== null) {
        const precedingText = value.slice(cursor, match.index);
        if (precedingText && consumeText(precedingText)) {
            break;
        }
        cursor = tagPattern.lastIndex;

        const tag = match[CROP_ZERO];
        output += tag;
        updateOpenTags(tag, openTags);

        if (visibleCount >= maxLength && maxLength > CROP_ZERO) {
            // Remaining content is all beyond the limit; stop and close tags.
            cropped = getVisibleTextLength(value) > maxLength;
            break;
        }
    }

    if (!cropped) {
        const trailingText = value.slice(cursor);
        if (trailingText) {
            consumeText(trailingText);
        }
    }

    // Close any tags left open, innermost first.
    for (let i = openTags.length - CROP_ONE; i >= CROP_ZERO; i--) {
        output += `</${openTags[i]}>`;
    }

    return output;
}

/**
 * Updates the open-tag stack for a single tag token. Open tags are pushed,
 * matching close tags pop, and void/self-closing tags are ignored.
 * @param tag a full tag token including angle brackets, e.g. "<b>"
 * @param openTags the mutable stack of currently open tag names
 */
function updateOpenTags(tag: string, openTags: string[]): void {
    const closeMatch = tag.match(/^<\/\s*([a-zA-Z][a-zA-Z0-9]*)/);
    if (closeMatch) {
        const name = closeMatch[1].toLowerCase();
        const lastIndex = openTags.lastIndexOf(name);
        if (lastIndex !== CROP_NOT_FOUND) {
            openTags.splice(lastIndex, CROP_ONE);
        }
        return;
    }

    const openMatch = tag.match(/^<\s*([a-zA-Z][a-zA-Z0-9]*)/);
    if (!openMatch) {
        return;
    }
    const name = openMatch[1].toLowerCase();
    const isSelfClosing = /\/\s*>$/.test(tag);
    if (isSelfClosing || VOID_HTML_TAGS.has(name)) {
        return;
    }
    openTags.push(name);
}
